import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ThreadMessageLike } from '@assistant-ui/react';
import type { UIEvent } from '../types/ui-events';
import type { Canvas, Surface } from '../types/canvases';
import { spaceApi, meApi } from '../services/api';
import { convertMessages, findLastCanvasEvent, findUnansweredAskRef } from '../utils/conversation';
import { ASK_USER_TOOL_NAME, askUserToolCallId } from '../utils/run-stream-state';
import { runStateStore, activeFocusedRunIdStore } from './useAsyncInvokeAdapter';
import { delegatedTaskSummaryStore, clearDelegatedTaskCaches } from './useDelegatedTaskStatus';
import type { DelegatedTask } from '../types/delegated-tasks';

export interface ConversationRestoreOptions {
  conversationId?: string;
  /** Space the conversation lives in. Required — backend rule: every
   *  single-entity GET goes through `/spaces/{spaceId}/conversations/{id}`. */
  spaceId?: string;
  dispatchUIEvent?: (event: UIEvent) => void;
  /** Event types to restore from canvas (default: show_entity + show_artifact) */
  canvasEventTypes?: readonly string[];
  /** Whether to fetch full run details for the last assistant message (for timeline restoration). Default: false */
  fetchRunDetails?: boolean;
  /** Whether to fetch delegated tasks and pre-seed terminal task summaries. Default: false */
  fetchDelegatedTasks?: boolean;
}

export interface CanvasHint {
  canvasId: string;
  title: string;
  isDefault: boolean;
  layout?: string;
  order?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ConversationRestoreResult {
  messages: ThreadMessageLike[] | undefined;
  canvasHints: CanvasHint[];
  defaultCanvas: { canvas: Canvas; surfaces: Surface[] } | null;
  loading: boolean;
  /**
   * Set when the conversation reloaded mid-run: the last persisted message is a
   * USER turn that carries a `runId` (the assistant turn persists only at
   * run_finished). Consumers (`useResumeInFlightRun`) use this to re-attach the
   * still-executing run so its live trace resumes instead of looking lost.
   */
  inFlightRun?: { runId: string; parentMessageId: string };
}

/**
 * Fetches and converts a saved conversation into ThreadMessageLike[].
 * Handles cancel tokens, canvas event replay, and run duration restoration.
 *
 * Used by both SandboxConversation and BuilderContent to avoid duplicating
 * the async fetch + convert + dispatch pattern.
 */
export function useConversationRestore({
  conversationId,
  spaceId,
  dispatchUIEvent,
  canvasEventTypes,
  fetchRunDetails = false,
  fetchDelegatedTasks = false,
}: ConversationRestoreOptions): ConversationRestoreResult {
  const [messages, setMessages] = useState<ThreadMessageLike[] | undefined>(undefined);
  const [canvasHints, setCanvasHints] = useState<CanvasHint[]>([]);
  const [defaultCanvas, setDefaultCanvas] = useState<{ canvas: Canvas; surfaces: Surface[] } | null>(null);
  const [loading, setLoading] = useState(!!conversationId);
  const [inFlightRun, setInFlightRun] = useState<{ runId: string; parentMessageId: string } | undefined>(undefined);
  const fetchingRef = useRef(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!conversationId) {
      dispatchUIEvent?.({ eventType: 'clear_canvas', data: {} });
      // No conversation = nothing for the context-utilization bar to track.
      activeFocusedRunIdStore.set(undefined);
      setInFlightRun(undefined);
      return;
    }
    if (!spaceId) {
      // Wait until the caller can supply the conversation's owning space —
      // the space-scoped endpoint requires it.
      return;
    }
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    let cancelled = false;

    const api = spaceApi(spaceId);

    (async () => {
      try {
        // Read through React Query's cache under the SAME key `useConversation`
        // uses, so the conversation is fetched ONCE and shared — instead of a
        // separate raw fetch that duplicated the network call (and doubled
        // again under StrictMode). ensureQueryData dedupes in-flight requests
        // and respects staleTime, so concurrent consumers collapse to one GET.
        const conversation = await queryClient.ensureQueryData({
          queryKey: ['space', spaceId, 'conversations', conversationId],
          queryFn: () => api.conversations.getConversation(conversationId),
        });
        if (cancelled) return;

        // Detect an in-flight run from the RAW messages (the converted
        // ThreadMessageLike[] drops the user-message runId). Mid-run the last
        // persisted message is the user turn stamped with the active runId; its
        // messageId is the assistant-ui parent for the resumed run (convertNewFormat
        // sets id: messageId, and a composer user turn is never filtered).
        const lastMsg = conversation.messages[conversation.messages.length - 1];
        setInFlightRun(
          lastMsg && lastMsg.role === 'user' && lastMsg.runId
            ? { runId: lastMsg.runId, parentMessageId: lastMsg.messageId }
            : undefined,
        );

        // Optionally fetch last run's steps for timeline restoration
        let runSteps: import('../types').RunStepResponse[] | undefined;
        if (fetchRunDetails) {
          const lastAssistant = [...conversation.messages].reverse().find((m) => m.role === 'assistant');
          if (lastAssistant?.runId) {
            try {
              const run = await api.runs.get(lastAssistant.runId);
              runSteps = run.steps;
            } catch (err) {
              console.warn('[useConversationRestore] Failed to load run details', lastAssistant.runId, err);
            }
          }
        }
        if (cancelled) return;

        const cvStubs = conversation.canvases ?? [];
        if (cvStubs.length === 0 && conversation.messages.length > 0) {
          console.warn('[useConversationRestore] Conversation has messages but no canvas hints — surfaces will not restore', conversationId);
        }
        setCanvasHints(cvStubs);

        // Eagerly fetch the default canvas's surfaces + delegated tasks in parallel
        const defaultStub = cvStubs.find((w) => w.isDefault) ?? cvStubs[0];
        const [fetchedCanvas, tasks] = await Promise.all([
          defaultStub
            ? api.canvases.get(conversationId, defaultStub.canvasId).catch(() => null)
            : Promise.resolve(null),
          fetchDelegatedTasks
            ? api.delegatedTasks.list(conversationId).catch(() => [] as DelegatedTask[])
            : Promise.resolve([] as DelegatedTask[]),
        ]);
        if (cancelled) return;

        // Note: DelegatedTaskCard node injection removed — TaskDashboard
        // component now handles task discovery internally via API + WebSocket.

        if (fetchedCanvas) {
          setDefaultCanvas(fetchedCanvas);
        }

        // Populate the summary store for terminal tasks so cards skip getRunStatus()
        delegatedTaskSummaryStore.clear();
        for (const task of tasks) {
          const isTerminal = task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled';
          if (task.delegatedRunId && isTerminal) {
            delegatedTaskSummaryStore.set(task.delegatedRunId, {
              durationMs: task.summary?.durationMs ?? task.durationMs,
              toolCallCount: task.summary?.toolCallCount,
              usage: task.summary?.usage,
              responsePreview: task.summary?.responsePreview ?? task.response?.slice(0, 200),
              error: task.summary?.error ?? task.error,
              status: task.status,
              updatedAt: task.updatedAt,
            });
          }
        }

        const converted = convertMessages(conversation.messages, runSteps);

        // Restore per-runId UI state for the LAST assistant message's run
        // so the CoT collapses to "Thought for Xs" and the context bar
        // reflects the restored conversation's most recent state. Earlier
        // messages already carry per-message metadata (runDurationMs, runId)
        // which the CoT reads directly without the store.
        const lastAssistant = [...conversation.messages].reverse().find((m) => m.role === 'assistant');
        const lastRunId = lastAssistant?.runId;
        if (lastRunId) {
          runStateStore.updateProgress(lastRunId, {
            hasFinalAnswer: true,
            isRunComplete: true,
          });
          if (converted.runDurationMs != null) {
            runStateStore.updateDuration(lastRunId, converted.runDurationMs);
          }
          if (conversation.lastContextUtilization) {
            runStateStore.updateContextUtilization(lastRunId, {
              utilizationPercent: conversation.lastContextUtilization.utilizationPercent,
              compactionThresholdPercent: conversation.lastContextUtilization.compactionThresholdPercent,
              compactionImminent: conversation.lastContextUtilization.compactionImminent,
            });
          }
          // Point the context-utilization bar at this run while the
          // conversation is the focused one. It'll be replaced when a new
          // run starts or the user navigates away.
          activeFocusedRunIdStore.set(lastRunId);
        }

        // Resurrect the pending ask_user card: the `__ask_user` part is
        // synthesized client-side during the live run and never persisted, so
        // a reload while the conversation is PARKED on a question would
        // otherwise restore only the AskUserSummary and leave the user unable
        // to answer inline. Confirm with the inbox item (authoritative status
        // — covers cross-tab answers and free-text dismissals) before
        // injecting; on any failure the sidebar inbox remains the fallback.
        const pendingAsk = findUnansweredAskRef(conversation.messages);
        if (pendingAsk) {
          try {
            const item = await meApi.inboxItem(pendingAsk.ref.inboxItemId, {
              expand: 'source',
            });
            const stillPending =
              item.status === 'pending' &&
              (item.source?.status ?? 'pending') === 'pending';
            if (stillPending && !cancelled) {
              const target = converted.messages.find((m) => m.id === pendingAsk.messageId);
              if (target) {
                const args = {
                  askUserRequestId: pendingAsk.ref.askUserRequestId,
                  inboxItemId: pendingAsk.ref.inboxItemId,
                  spaceId: item.spaceId ?? spaceId,
                  conversationId,
                  questions: item.source?.questions ?? pendingAsk.ref.snapshot.questions,
                  status: 'pending',
                };
                (target.content as unknown[]).push({
                  type: 'tool-call',
                  toolCallId: askUserToolCallId(pendingAsk.ref.askUserRequestId),
                  toolName: ASK_USER_TOOL_NAME,
                  args,
                  argsText: JSON.stringify(args),
                });
              }
            }
          } catch {
            // Best-effort — the sidebar inbox still surfaces the question.
          }
        }
        if (cancelled) return;

        setMessages(converted.messages);

        // Replay last canvas event to restore the panel
        const lastCanvas = findLastCanvasEvent(conversation.messages, canvasEventTypes);
        if (lastCanvas) {
          dispatchUIEvent?.(lastCanvas);
        }
      } catch (err) {
        console.warn('[useConversationRestore] Failed to load conversation', conversationId, err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; fetchingRef.current = false; delegatedTaskSummaryStore.clear(); clearDelegatedTaskCaches(); };
  // Re-run only on conversationId/spaceId — caller-controlled identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, spaceId]);

  return { messages, canvasHints, defaultCanvas, loading, inFlightRun };
}
